import { useEffect, useMemo, useState } from "react";

/* ------------------------------------------------------------------ *
 * SearchSelect — searchable dropdown with an inline "add new" form.
 *
 * One component, three uses on /new-order: funeral director, funeral
 * location, and the late person's name. Callers supply the data and the
 * per-use create form; everything visual lives here.
 *
 * Props
 *   items        [{ id, chip, primary, secondary, meta, group }]
 *   selectedId   currently chosen item id
 *   onSelect(id)
 *   onClose()
 *   placeholder  search field placeholder
 *   groups       [{ key, label, queryLabel }] — render order; queryLabel
 *                replaces label while a query is active
 *   filter(item, q)  optional; defaults to matching primary + secondary
 *   chipTone     'gold' | 'teal' | 'sand'
 *   add          { label, title, saveLabel, note, fields:[{label,name,placeholder,span,type}],
 *                  kindLabel, kinds:[string] }   omit to hide the add affordance
 *                `label` may be a function of the trimmed query.
 *                `direct: true` skips the add form: the bar (and ⏎ with no
 *                match under the cursor) calls onCreate straight away with
 *                the typed query, and is disabled until something is typed.
 *   onCreate(draft)  called with { values: {...}, kind, query } on save. May
 *                    return a promise — the save button waits on it and stays
 *                    in the add view if it rejects.
 *   direction    'down' (default) | 'up'
 *   loading      a search is in flight: shows a "Searching…" line above
 *                the current rows (they stay until the new ones arrive)
 *   onQuery(q)   notified on every keystroke, for server-backed searches. When
 *                supplied, local filtering is skipped — `items` is already the
 *                result set for the current query.
 * ------------------------------------------------------------------ */

const CHIP_TONES = {
  gold: { background: "var(--rf-brand-tint)", color: "var(--rf-brand-deep)" },
  teal: { background: "var(--rf-accent-tint)", color: "var(--rf-accent)" },
  sand: { background: "#f0e3c8", color: "var(--rf-brand-deep)" },
};

function defaultFilter(item, q) {
  return (
    `${item.primary} ${item.secondary || ""} ${item.chip || ""}`
      .toLowerCase()
      .indexOf(q) !== -1
  );
}

export function SearchSelect({
  items,
  selectedId,
  onSelect,
  onClose,
  placeholder = "Search",
  groups = [],
  filter = defaultFilter,
  chipTone = "gold",
  add,
  onCreate,
  direction = "down",
  loading = false,
  onQuery,
}) {
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState("");
  const [values, setValues] = useState({});
  const [cursor, setCursor] = useState(0);
  const [saving, setSaving] = useState(false);

  const q = query.trim().toLowerCase();
  const serverSide = typeof onQuery === "function";

  /* Server-backed uses re-fetch as you type; a short debounce keeps the
     funeral search from firing a request per keystroke. */
  useEffect(() => {
    if (!serverSide) return undefined;
    const timer = setTimeout(() => onQuery(query.trim()), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const matched = useMemo(
    () => (q && !serverSide ? items.filter((it) => filter(it, q)) : items),
    [items, q, filter, serverSide]
  );

  const rendered = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      const rows = matched.filter((it) => it.group === g.key);
      if (rows.length) {
        out.push({
          label: q && g.queryLabel ? g.queryLabel : g.label,
          items: rows,
        });
      }
    });
    // anything whose group wasn't declared still shows
    const known = new Set(groups.map((g) => g.key));
    const orphans = matched.filter((it) => !known.has(it.group));
    if (orphans.length) out.push({ label: q ? "Other" : "All", items: orphans });
    return out;
  }, [matched, groups, q]);

  const flat = useMemo(() => rendered.flatMap((g) => g.items), [rendered]);
  /* row -> its position in `flat`, for the cursor. Looked up per row on
     every render (each keystroke and hover), so a Map, not flat.indexOf —
     that made the list quadratic in its length. */
  const position = useMemo(
    () => new Map(flat.map((it, index) => [it, index])),
    [flat]
  );

  function onKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => Math.min(flat.length - 1, c + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const pick = flat[cursor];
      if (pick) onSelect(pick.id, pick);
      else if (canCreateDirect) commit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  }

  /* the typed query is enough to create — no form, no second name field */
  const canCreateDirect = Boolean(add && add.direct && q);

  function save(event) {
    event.preventDefault();
    commit();
  }

  function commit() {
    if (typeof onCreate !== "function") {
      setAdding(false);
      return;
    }
    const done = () => {
      setSaving(false);
      setAdding(false);
      setValues({});
      setKind("");
    };
    const result = onCreate({ values, kind, query });
    if (result && typeof result.then === "function") {
      setSaving(true);
      result.then(done).catch(() => setSaving(false));
    } else {
      done();
    }
  }

  const tone = CHIP_TONES[chipTone] || CHIP_TONES.gold;

  return (
    <div className={`rf-ss rf-ss-${direction}`}>
      {!adding && (
        <>
          <div className="rf-ss-search">
            <div className="rf-ss-field">
              <span className="rf-ss-icon" />
              <input
                autoFocus
                value={query}
                placeholder={placeholder}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setCursor(0);
                }}
                onKeyDown={onKeyDown}
              />
              <button
                type="button"
                className="rf-ss-clear"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>

          <div className="rf-ss-list">
            {/* the rows stay put while a search is in flight: emptying the
                list and rebuilding it on every pause in typing is what
                made the late-person box stutter */}
            {loading && <div className="rf-ss-loading">Searching…</div>}

            {rendered.map((group) => (
              <div key={group.label}>
                <div className="rf-ss-grouplabel">{group.label}</div>
                {group.items.map((it) => {
                  const index = position.get(it);
                  const active = index === cursor;
                  return (
                    <div
                      key={it.id}
                      className={[
                        "rf-ss-row",
                        it.id === selectedId ? "is-selected" : "",
                        active ? "is-cursor" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => onSelect(it.id, it)}
                      onMouseEnter={() => setCursor(index)}
                    >
                      <span className="rf-ss-chip" style={tone}>
                        {it.chip}
                      </span>
                      <div className="rf-ss-text">
                        <div className="rf-ss-primary">{it.primary}</div>
                        <div className="rf-ss-secondary">{it.secondary}</div>
                      </div>
                      <span className="rf-ss-meta">{it.meta}</span>
                      <span className="rf-ss-tick">
                        {it.id === selectedId ? "✓" : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}

            {!loading && rendered.length === 0 && (
              <div className="rf-ss-empty">No match for “{query}”</div>
            )}
          </div>

          {add && (
            <button
              type="button"
              className="rf-ss-addbar"
              disabled={Boolean(add.direct) && !canCreateDirect}
              onClick={() => (add.direct ? commit() : setAdding(true))}
            >
              <span className="rf-ss-plus">+</span>
              <span className="rf-ss-addlabel">
                {typeof add.label === "function" ? add.label(query.trim()) : add.label}
              </span>
              <span className="rf-ss-spacer" />
              <span className="rf-ss-hint">
                {canCreateDirect && flat.length === 0
                  ? "⏎ to create"
                  : "↑↓ to move · ⏎ to select"}
              </span>
            </button>
          )}
        </>
      )}

      {adding && add && (
        <form onSubmit={save} className="rf-ss-addform">
          <div className="rf-ss-addhead">
            <button
              type="button"
              className="rf-ss-back"
              onClick={() => setAdding(false)}
            >
              ← Back
            </button>
            <span className="rf-ss-addtitle">{add.title}</span>
          </div>

          <div className="rf-ss-addbody">
            <div className="rf-ss-addgrid">
              {add.fields.map((f) => {
                const key = f.name || f.label;
                return (
                  <div
                    key={key}
                    className="rf-ss-addfield"
                    style={{
                      gridColumn: f.span === "span 2" ? "span 2" : "auto",
                    }}
                  >
                    <label>{f.label}</label>
                    <input
                      type={f.type || "text"}
                      placeholder={f.placeholder}
                      value={values[key] || ""}
                      onChange={(e) =>
                        setValues((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    />
                  </div>
                );
              })}
            </div>

            {add.kinds && add.kinds.length > 0 && (
              <div className="rf-ss-kinds">
                <label>{add.kindLabel}</label>
                <div className="rf-ss-kindrow">
                  {add.kinds.map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`rf-chip-btn${kind === k ? " is-active" : ""}`}
                      onClick={() => setKind((prev) => (prev === k ? "" : k))}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {add.note && <p className="rf-ss-note">{add.note}</p>}
          </div>

          <div className="rf-ss-addfoot">
            <button
              type="button"
              className="rf-btn rf-btn-ghost"
              onClick={() => setAdding(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rf-btn rf-btn-primary rf-ss-save"
              disabled={saving}
            >
              {saving ? "Saving…" : add.saveLabel}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default SearchSelect;
